import { useTranslation } from 'react-i18next'

import {
  type MovePlace,
  type MoveStop,
} from '../../../modules/moves'
import { formatPlace } from '../../../common/place'
import { Value, BooleanValue } from '../../data'
import { mergeClassName } from '../../../utils/mergeClassName'

export enum AddressRowType {
  Origin = 'origin',
  Destination = 'destination',
  Stop = 'stop',
}

type AddressRowProps = {
  type: AddressRowType
  data: MovePlace | MoveStop
}

const AddressRow: React.FC<AddressRowProps> = ({
  type,
  data,
}) => {
  const { t } = useTranslation()

  const getTitle = () => {
    switch (type) {
      case AddressRowType.Origin:
        return t('components.move.addresses.movingFrom')
      case AddressRowType.Destination:
        return t('components.move.addresses.movingTo')
      case AddressRowType.Stop:
        return (data as MoveStop).description
    }
  }

  return (
    <div className="h-full flex-1">
      <h3 className="my-2 font-bold text-black">
        { getTitle() }
        { 'stopDuration' in data && (
          <>
            { ' ' }
            ({ data.stopDuration }
            { t('units.hour') })
          </>
        ) }
      </h3>

      <div className={mergeClassName(
        'rounded-md border p-4 shadow lg:border-none',
      )}
      >
        <Value className="whitespace-pre-wrap">
          { formatPlace(
            data.address,
            {
              addressOnly: true,
              compact: true,
            },
          ).join('\n') }
        </Value>

        <div className="my-4">
          { (data?.nbStairs ?? 0) > 0
            ? (
              <>
                <span className="font-body2 text-sm text-black">
                  { t('components.move.addresses.nbStairs') }
                </span>
                { ': ' }
                { data.nbStairs }
              </>
              )
            : (
              <BooleanValue
                value={false}
                labelFalse={t('components.move.addresses.noStairs')}
              />
              ) }
        </div>

        { data.elevatorReservationRequired !== null && (
          <div className="my-4">
            <BooleanValue
              value={data.elevatorReservationRequired}
              labelTrue={t('components.move.addresses.elevatorNeeded')}
              labelFalse={t('components.move.addresses.elevatorNotNeeded')}
            />
          </div>
        ) }

      </div>
    </div>
  )
}

export default AddressRow
